import React, { useContext, useMemo, useState } from 'react';
import { getDashboardType, getPathFromDashboard } from '@/pages/dashboard/utils/utils';
import { PAGE_TYPE_AUTH_PATH } from '@/pages/dashboard/_config/constant';
import { IframeUrl } from '@/pages/home/components/IframeUrl';
import { buriedPoint, typeIdObj } from '@/pages/home/utils/buriedPoint';
import { StudioContext } from '../context';
import StudioState from '../components/StudioState';
import FullScreenBtn from '../components/FullScreenBtn';
import Loading from '@/pages/dashboard/components/Loadings';
import useAuthPathReport from '@/pages/home/hooks/useAuthPathReport';
import {
  resolveAuthPathIframeHeight,
  type IAuthPathIframeConfig,
  type TAuthPathIframeHeight,
} from './configUtils';
import './index.scss';

export interface IAuthPathProps {
  menu?: Record<string, any>;
  game?: Record<string, any>;
  logConfig?: {
    pageId?: string;
    pageName?: string;
    pagePath?: string;
  };
  /** multiTab 场景：token 请求的 page_type */
  page_type?: string;
  /** multiTab 场景：token 请求的 path，如 /multiReports/cbt */
  path?: string;
  /**
   * 直接传入的 iframe 高度。
   * 优先级：页面配置 config.iframeHeight > 本 props > 管理端 menu.iframe_height。
   */
  iframeHeight?: TAuthPathIframeHeight;
  /** CommonPageRender 从页面配置的 config 字段透传 */
  config?: IAuthPathIframeConfig;
}

const AuthPathContent: React.FC<IAuthPathProps> = ({
  menu,
  game,
  logConfig,
  page_type: tokenPageType,
  path: tokenPath,
  iframeHeight,
  config,
}) => {
  const [loadedAuthUrl, setLoadedAuthUrl] = useState<string>('');
  const {
    currentGame: contextGame,
    currentMenu: contextMenu,
    menuPermission,
  } = useContext(StudioContext);
  const currentMenu = menu || contextMenu;
  const currentGame = game || contextGame;
  const effectiveIframeHeight = resolveAuthPathIframeHeight(
    config?.iframeHeight,
    iframeHeight,
    currentMenu?.iframe_height,
  );
  const iframeStyle = useMemo(() => (
    effectiveIframeHeight
      ? { height: effectiveIframeHeight, minHeight: effectiveIframeHeight }
      : undefined
  ), [effectiveIframeHeight]);

  const { loading, errorKey, authUrl } = useAuthPathReport({
    currentMenu,
    currentGame,
    getAuthPath: (menuItem) => menuItem.path || '',
    getMenuId: (menuItem) => String(menuItem.id || menuItem.u_id || ''),
    getPageType: (menuItem) => menuItem.page_type || PAGE_TYPE_AUTH_PATH,
    canOpen: ({ menu: menuItem, game: gameItem }) => {
      if (!menuItem || !gameItem) {
        return { ok: false, errorKey: 'authpath_error_menu_mismatch' as const };
      }
      if (getDashboardType(menuItem) !== PAGE_TYPE_AUTH_PATH) {
        return { ok: false, errorKey: 'authpath_error_menu_mismatch' as const };
      }
      return { ok: true };
    },
    buildTokenParams: ({ game: gameItem, authPath, menuId, pageType }) => {
      const baseParams = {
        res_id: 'studio_hub' as const,
        studio_code: gameItem?.studio_code || '',
        game_code: gameItem?.game_code || gameItem?.unified_edition_id || '',
        menu_id: menuId,
      };
      if (tokenPageType && tokenPath) {
        return {
          ...baseParams,
          page_type: tokenPageType,
          path: tokenPath,
          page_value: authPath,
        };
      }
      return {
        ...baseParams,
        page_type: pageType,
        page_value: authPath,
      };
    },
    onSuccess: ({ menu: menuItem, game: gameItem }) => {
      const { id = '', name_en: nameEn } = menuItem || {};
      buriedPoint({
        pageId: logConfig?.pageId || '100615',
        menuId: id,
        pageName: logConfig?.pageName || nameEn,
        extInfo: logConfig?.pageName || nameEn,
        pagePath: logConfig?.pagePath || getPathFromDashboard(menuItem),
        uid: gameItem?.game_code || gameItem?.unified_edition_id || '',
        typeId: typeIdObj.studio,
        gameName: gameItem?.game_name,
      });
    },
  });

  if (!menu && menuPermission === false) {
    return <StudioState isNoPermission />;
  }

  if (loading) return <Loading />;
  if (errorKey) return <StudioState />;
  if (!authUrl) return <StudioState />;

  return (
    <FullScreenBtn
      className="dbh-studio-auth-path-fullscreen"
      showButton={loadedAuthUrl === authUrl}
      style={iframeStyle}
    >
      <IframeUrl
        url={authUrl}
        className="db-studio-iframe-wrap dbh-studio-auth-path"
        style={iframeStyle}
        onLoad={(event) => {
          if (event.currentTarget.getAttribute('src')) {
            setLoadedAuthUrl(authUrl);
          }
        }}
      />
    </FullScreenBtn>
  );
};

export default AuthPathContent;
